import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle, TAGLINE } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const email = params.get("email") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const pwErrors = useMemo(() => {
    const errs = [];
    if (pw && pw.length < 8) errs.push("At least 8 characters");
    if (pw && !/[A-Za-z]/.test(pw)) errs.push("Include a letter");
    if (pw && !/\d/.test(pw)) errs.push("Include a number");
    return errs;
  }, [pw]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing or invalid reset link.");
      return;
    }
    if (pw !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    if (pwErrors.length) {
      toast.error(pwErrors[0]);
      return;
    }
    setLoading(true);
    const res = await resetPassword(token, pw);
    setLoading(false);
    if (res.ok) {
      setDone(true);
      toast.success("Password reset. You can sign in now.");
      setTimeout(() => navigate("/login"), 1200);
    } else {
      toast.error(res.error || "Reset failed.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
      <header className="w-full max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/login" className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white" data-testid="reset-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <ThemeToggle />
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-10">
            <img src="/certiseal-logo.png" alt="CertiSeal" className="w-16 h-16 mx-auto mb-5 rounded-full dark:bg-white dark:p-1" />
            <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-slate-900 dark:text-white">Choose a new password</h1>
            <p className="mt-2 text-slate-600 dark:text-slate-400">{TAGLINE}</p>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8">
            {!token ? (
              <div className="text-center space-y-3">
                <p className="text-sm text-slate-600 dark:text-slate-400">This reset link is missing its token. Please request a new one.</p>
                <Link to="/forgot-password">
                  <Button variant="outline" className="dark:border-slate-700 dark:text-slate-100">Request a new link</Button>
                </Link>
              </div>
            ) : done ? (
              <div className="text-center space-y-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
                  <ShieldCheck className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h2 className="font-display font-bold text-xl text-slate-900 dark:text-white">Password updated</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">Redirecting to sign in…</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {email && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Resetting password for <span className="font-medium text-slate-900 dark:text-white">{email}</span>
                  </p>
                )}
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">New password</label>
                  <Input
                    type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 8 characters" required minLength={8}
                    className="h-12 border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">Confirm password</label>
                  <Input
                    type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Retype the new password" required minLength={8}
                    className="h-12 border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    data-testid="reset-confirm-input"
                  />
                </div>
                {pwErrors.length > 0 && (
                  <ul className="text-xs text-amber-600 dark:text-amber-400 space-y-1">
                    {pwErrors.map((e, i) => (<li key={i}>• {e}</li>))}
                  </ul>
                )}
                <Button
                  type="submit" disabled={loading}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] font-display font-semibold"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Resetting…" : "Reset password"}
                </Button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
